import { FC, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Heading,
  Text,
  Image,
  Container,
  Link,
  AspectRatio,
  Alert,
  AlertIcon,
  AlertDescription,
  List,
  ListItem,
} from "@chakra-ui/react";
import { blogPosts } from "./blogData";
import SEO from "../../SEO";

type BlogPost = {
  id: number;
  title: string;
  content: Array<{
    type:
      | "header"
      | "text"
      | "image"
      | "link"
      | "video"
      | "linkText"
      | "subHeader"
      | "blockquote"
      | "warningAlert"
      | "list";
    content: string;
    link?: string;
    imageWidth?: string;
    subContent?: string;
    items?: Record<string, string>;
  }>;
  thumbnail: string;
  slug: string;
};

const SingleBlogPage: FC = () => {
  const { title } = useParams<{ title: string }>();
  const navigate = useNavigate();

  const blogPost = blogPosts.find((post: BlogPost) => post.slug === title);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [title]);

  if (!blogPost || blogPost.slug !== title) {
    return (
      <Box textAlign="center" py={16}>
        <Heading as="h1" mb={4}>
          Cikk nem található
        </Heading>
        <Link onClick={() => navigate("/blog")}>
          <Box
            as="button"
            px={{ base: 4, md: 5 }}
            py={{ base: 2, md: 3 }}
            fontWeight="600"
            fontSize={{ base: "sm", md: "md" }}
            borderRadius="full"
            color="white"
            position="relative"
            overflow="hidden"
            bg="rgba(0, 113, 136, 0.85)"
            backdropFilter="blur(6px)"
            border="1px solid rgba(255,255,255,0.15)"
            boxShadow="0 6px 16px rgba(0,113,136,0.25)"
            transition="all 0.25s ease"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            _hover={{
              transform: "translateY(-2px) scale(1.02)",
              boxShadow: "0 10px 22px rgba(0,113,136,0.35)",
              bg: "rgba(0, 113, 136, 0.95)",
            }}
            _active={{
              transform: "scale(0.98)",
              boxShadow: "0 4px 12px rgba(0,113,136,0.2)",
            }}
          >
            ←&nbsp;Vissza a blog oldalra
          </Box>
        </Link>
      </Box>
    );
  }

  const blogPostingJsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: blogPost.title,
    image: blogPost.thumbnail,
    url: `https://wobble.hu/blog/${blogPost.slug}`,
    mainEntityOfPage: `https://wobble.hu/blog/${blogPost.slug}`,
    description: blogPost.content
      .map((block) => block.content)
      .join(" ")
      .slice(0, 300),
    publisher: {
      "@type": "Organization",
      name: "Wobble",
      logo: {
        "@type": "ImageObject",
        url: "https://wobble.hu/wobble_icon.png",
      },
    },
    author: {
      "@type": "Organization",
      name: "Wobble",
    },
  };

  return (
    <Box bgGradient="linear(#5AB3C5, #E9F0F1)">
      <SEO
        title={`${blogPost.title} - Wobble Blog`}
        description={blogPost.content.map((block) => block.content).join(" ")}
        keywords="blog, online megjelenés, kisvállalkozás, weboldal készítés, foglalási rendszer, időpontfoglaló"
        ogTitle={blogPost.title}
        ogDescription={blogPost.content.map((block) => block.content).join(" ")}
        ogImage={blogPost.thumbnail}
        jsonLd={blogPostingJsonLd}
      />
      <Container
        maxW="900px"
        mx="auto"
        py={8}
        px={4}
        bg="gray.50"
        borderRadius="lg"
        boxShadow="md"
      >
        <Link onClick={() => navigate("/blog")}>
          <Box
            as="button"
            px={{ base: 4, md: 5 }}
            py={{ base: 2, md: 3 }}
            fontWeight="600"
            fontSize={{ base: "sm", md: "md" }}
            borderRadius="full"
            color="white"
            position="relative"
            overflow="hidden"
            bg="rgba(0, 113, 136, 0.85)"
            backdropFilter="blur(6px)"
            border="1px solid rgba(255,255,255,0.15)"
            boxShadow="0 6px 16px rgba(0,113,136,0.25)"
            transition="all 0.25s ease"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            _hover={{
              transform: "translateY(-2px) scale(1.02)",
              boxShadow: "0 10px 22px rgba(0,113,136,0.35)",
              bg: "rgba(0, 113, 136, 0.95)",
            }}
            _active={{
              transform: "scale(0.98)",
              boxShadow: "0 4px 12px rgba(0,113,136,0.2)",
            }}
          >
            ←&nbsp;Vissza a blog oldalra
          </Box>
        </Link>

        {/* Thumbnail Image */}
        <Image
          src={blogPost.thumbnail}
          alt={blogPost.title}
          borderRadius="md"
          width="100%"
          maxHeight="500px"
          objectFit="cover"
          mb={6}
          mt={8}
        />

        {/* Blog Title */}
        <Heading
          as="h1"
          mt={6}
          mb={4}
          color="#007188"
          fontSize={{ base: "3xl", md: "4xl" }}
          textAlign="center"
        >
          {blogPost.title}
        </Heading>

        {/* Render content blocks */}
        {blogPost.content.map((block, index) => {
          switch (block.type) {
            case "header":
              return (
                <Heading
                  key={index}
                  as="h2"
                  fontSize={{ base: "xl", md: "2xl" }}
                  mt={10}
                  mb={8}
                  color="#007188"
                  textAlign="left"
                >
                  {block.content}
                </Heading>
              );
            case "subHeader":
              return (
                <Heading
                  key={index}
                  as="h3"
                  fontSize={{ base: "lg", md: "xl" }}
                  mt={10}
                  mb={8}
                  color="#007188"
                  textAlign="left"
                >
                  {block.content}
                </Heading>
              );
            case "blockquote":
              return (
                <Box
                  key={index}
                  as="blockquote"
                  borderLeft="4px solid"
                  borderColor="gray.300"
                  pl={4}
                  my={4}
                  fontStyle="italic"
                  color="gray.600"
                >
                  <Text>{block.content}</Text>
                  {block.subContent && (
                    <>
                      <br />
                      <Text>{block.subContent}</Text>
                    </>
                  )}
                </Box>
              );
            case "warningAlert":
              return (
                <Alert
                  key={index}
                  status="warning"
                  variant="left-accent"
                  mb={4}
                >
                  <AlertIcon />
                  <AlertDescription>{block.content}</AlertDescription>
                </Alert>
              );
            case "list":
              return (
                <Box key={index} my={4}>
                  <Text
                    mb={2}
                    fontWeight="bold"
                    fontSize={{ base: "md", lg: "lg" }}
                    color="#024856"
                  >
                    {block.content}
                  </Text>
                  <List spacing={3} styleType="disc" ml={2} pl={4}>
                    {block.items &&
                      Object.entries(block.items).map(([key, value]) => (
                        <ListItem key={key} color="#024856">
                          <Text as="span" fontWeight="semibold" color="#024856">
                            {key}:
                          </Text>{" "}
                          {value}
                        </ListItem>
                      ))}
                  </List>
                </Box>
              );
            case "text":
              return (
                <Text
                  key={index}
                  fontSize={{ base: "md", lg: "lg" }}
                  color="#024856"
                  mb={6}
                  mt={8}
                  lineHeight="1.7"
                  textAlign="justify"
                  _notFirst={{ mt: 4 }}
                >
                  {block.content}
                </Text>
              );
            case "image":
              return (
                <Image
                  key={index}
                  src={block.content}
                  alt={block.content}
                  borderRadius="md"
                  width={block.imageWidth ?? "70%"}
                  mb={6}
                  objectFit="cover"
                />
              );
            case "link":
              return (
                <Link
                  _hover={{ textDecoration: "underline", color: "#007188" }}
                  key={index}
                  fontSize={{ base: "md", lg: "lg" }}
                  color="#024856"
                  mb={4}
                  lineHeight="1.7"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={block.content}
                >
                  {block.content}
                </Link>
              );
            case "linkText":
              return (
                <Box mb={6}>
                  <Link
                    _hover={{ textDecoration: "underline", color: "#007188" }}
                    key={index}
                    fontSize={{ base: "md", lg: "lg" }}
                    color="#024856"
                    mt={6}
                    mb={4}
                    lineHeight="1.7"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={block.link}
                  >
                    {block.content}
                  </Link>
                </Box>
              );
            case "video":
              return (
                <AspectRatio key={index} maxW="560px" ratio={16 / 9} mb={6}>
                  <video controls>
                    <source src={block.content} type="video/mp4" />
                  </video>
                </AspectRatio>
              );
            default:
              return null;
          }
        })}
        <Link onClick={() => navigate("/blog")}>
          <Box
            as="button"
            px={{ base: 4, md: 5 }}
            py={{ base: 2, md: 3 }}
            fontWeight="600"
            fontSize={{ base: "sm", md: "md" }}
            borderRadius="full"
            color="white"
            position="relative"
            overflow="hidden"
            bg="rgba(0, 113, 136, 0.85)"
            backdropFilter="blur(6px)"
            border="1px solid rgba(255,255,255,0.15)"
            boxShadow="0 6px 16px rgba(0,113,136,0.25)"
            transition="all 0.25s ease"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            _hover={{
              transform: "translateY(-2px) scale(1.02)",
              boxShadow: "0 10px 22px rgba(0,113,136,0.35)",
              bg: "rgba(0, 113, 136, 0.95)",
            }}
            _active={{
              transform: "scale(0.98)",
              boxShadow: "0 4px 12px rgba(0,113,136,0.2)",
            }}
          >
            ←&nbsp;Vissza a blog oldalra
          </Box>
        </Link>
      </Container>
    </Box>
  );
};

export default SingleBlogPage;
